import axios from 'axios'
import type {
  SuperAdminLoginResponse,
  SuperAdmin,
  ClientListItem,
  ClientDetail,
  ClientCreatePayload,
  ClientUpdatePayload,
  FeatureActivationPayload,
  AdminUpdatePayload,
  ResetPasswordResponse,
  AuditEvent,
  AvailableFeature,
  MasterDashboardData,
  MasterMessagingIdentity,
  KafiOverview,
  KafiRankingRow,
} from '@/types/master'

let _masterToken: string | null = null
let _masterRefreshToken: string | null = null

// Restore persisted tokens on module load (survives page refresh)
// Using sessionStorage instead of localStorage to limit XSS exposure
const _persistedToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('master_token') : null
if (_persistedToken) {
  _masterToken = _persistedToken
}
const _persistedRefreshToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('master_refresh_token') : null
if (_persistedRefreshToken) {
  _masterRefreshToken = _persistedRefreshToken
}

export function setMasterToken(token: string | null) {
  _masterToken = token
  if (token) {
    sessionStorage.setItem('master_token', token)
  } else {
    sessionStorage.removeItem('master_token')
  }
}

export function getMasterToken(): string | null {
  return _masterToken
}

export function setMasterRefreshToken(refreshToken: string | null) {
  _masterRefreshToken = refreshToken
  if (refreshToken) {
    sessionStorage.setItem('master_refresh_token', refreshToken)
  } else {
    sessionStorage.removeItem('master_refresh_token')
  }
}

export function getMasterRefreshToken(): string | null {
  return _masterRefreshToken
}

export function clearMasterTokens() {
  _masterToken = null
  _masterRefreshToken = null
  sessionStorage.removeItem('master_token')
  sessionStorage.removeItem('master_refresh_token')
  sessionStorage.removeItem('master_name')
}

// Callback-based redirect — allows React Router navigation instead of full page reload.
// Registered by MasterAuthGuard on mount, cleared on unmount.
let _onMasterLogout: (() => void) | null = null

export function onMasterLogout(cb: () => void) {
  _onMasterLogout = cb
}

export function clearMasterLogout() {
  _onMasterLogout = null
}

// Uses VITE_API_URL env var if set, otherwise falls back to same-origin '/api' (H-18).
const masterApi = axios.create({ baseURL: import.meta.env.VITE_API_URL || '/api', headers: { 'Content-Type': 'application/json' }, timeout: 15000 })

masterApi.interceptors.request.use((config) => {
  if (_masterToken && config.headers) config.headers.Authorization = `Bearer ${_masterToken}`
  return config
})

// Tracks whether a token refresh is in progress to avoid concurrent refreshes
let _isRefreshing = false
let _refreshSubscribers: Array<(token: string) => void> = []

function onTokenRefreshed(newToken: string) {
  _refreshSubscribers.forEach((cb) => cb(newToken))
  _refreshSubscribers = []
}

masterApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      // If we have a refresh token, try refreshing before redirecting
      if (_masterRefreshToken && !originalRequest._isRefreshRequest) {
        if (_isRefreshing) {
          // Another refresh is in progress — queue this request
          return new Promise((resolve) => {
            _refreshSubscribers.push((newToken: string) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              resolve(masterApi(originalRequest))
            })
          })
        }

        originalRequest._retry = true
        _isRefreshing = true

        try {
          const { data } = await axios.post(
            `${import.meta.env.VITE_API_URL || '/api'}/master/auth/refresh`,
            {},
            { headers: { Authorization: `Bearer ${_masterRefreshToken}` } },
          )

          const newAccessToken: string = data.access_token
          const newRefreshToken: string = data.refresh_token

          setMasterToken(newAccessToken)
          setMasterRefreshToken(newRefreshToken)

          onTokenRefreshed(newAccessToken)

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          return masterApi(originalRequest)
        } catch {
          // Refresh failed — clear everything and redirect to login
          clearMasterTokens()
          _onMasterLogout?.()
          return Promise.reject(error)
        } finally {
          _isRefreshing = false
        }
      }

      // No refresh token or refresh failed — clear tokens and redirect
      clearMasterTokens()
      _onMasterLogout?.()
    }
    return Promise.reject(error)
  },
)

function toSearchParams(params: Record<string, string | number | boolean | undefined>): string {
  const s = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined) s.set(k, String(v)) })
  const qs = s.toString()
  return qs ? `?${qs}` : ''
}

export const masterService = {
  async login(email: string, password: string): Promise<SuperAdminLoginResponse> {
    const formData = new URLSearchParams()
    formData.set('username', email)
    formData.set('password', password)
    const { data } = await masterApi.post('/master/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    setMasterToken(data.access_token)
    setMasterRefreshToken(data.refresh_token)
    if (data.super_admin_name) {
      localStorage.setItem('master_name', data.super_admin_name)
    }
    return data
  },

  async logout(): Promise<void> {
    try {
      const refreshToken = getMasterRefreshToken()
      await masterApi.post('/master/auth/logout', {
        refresh_token: refreshToken || undefined,
      })
    } catch {
      // Ignore errors — best-effort token revocation on server
    } finally {
      clearMasterTokens()
    }
  },

  async getMe(): Promise<SuperAdmin> {
    const { data } = await masterApi.get('/master/auth/me')
    return data
  },

  async lookupMessagingIdentities(phone: string): Promise<MasterMessagingIdentity | null> {
    const { data } = await masterApi.get('/master/messaging/identities', { params: { phone } })
    return data
  },

  async releaseMessagingIdentity(identityId: string): Promise<void> {
    await masterApi.post(`/master/messaging/identities/${identityId}/release`)
  },

  async getClients(params?: { q?: string; is_active?: boolean; limit?: number; offset?: number }): Promise<{ items: ClientListItem[]; total: number; limit: number; offset: number }> {
    const qs = params ? toSearchParams({ q: params.q, is_active: params.is_active, limit: params.limit, offset: params.offset }) : ''
    const { data } = await masterApi.get(`/master/clients${qs}`)
    return { items: data.items || [], total: data.total || 0, limit: data.limit || 50, offset: data.offset || 0 }
  },

  async getClient(id: string): Promise<ClientDetail> {
    const { data } = await masterApi.get(`/master/clients/${id}`)
    return data
  },

  async createClient(payload: ClientCreatePayload): Promise<ClientDetail> {
    const { data } = await masterApi.post('/master/clients', payload)
    return data
  },

  async updateClient(id: string, payload: ClientUpdatePayload): Promise<void> {
    await masterApi.patch(`/master/clients/${id}`, payload)
  },

  async setClientStatus(id: string, is_active: boolean): Promise<void> {
    await masterApi.post(`/master/clients/${id}/status`, null, { params: { is_active } })
  },

  async activateFeature(tenantId: string, featureCode: string, months: number): Promise<void> {
    await masterApi.post(`/master/clients/${tenantId}/features/${featureCode}/activate`, { months } as FeatureActivationPayload)
  },

  async deactivateFeature(tenantId: string, featureCode: string): Promise<void> {
    await masterApi.post(`/master/clients/${tenantId}/features/${featureCode}/deactivate`)
  },

  async resetAdminPassword(tenantId: string): Promise<ResetPasswordResponse> {
    const { data } = await masterApi.post(`/master/clients/${tenantId}/admin/reset-password`)
    return data
  },

  async updateAdmin(tenantId: string, payload: AdminUpdatePayload): Promise<void> {
    await masterApi.patch(`/master/clients/${tenantId}/admin`, payload)
  },

  async getClientAudit(tenantId: string, limit = 30): Promise<AuditEvent[]> {
    const { data } = await masterApi.get(`/master/clients/${tenantId}/audit${toSearchParams({ limit })}`)
    return data.items || []
  },

  async getAvailableFeatures(): Promise<AvailableFeature[]> {
    const { data } = await masterApi.get('/master/available-features')
    return data
  },

  async getDashboard(): Promise<MasterDashboardData> {
    const { data } = await masterApi.get('/master/dashboard')
    return data
  },

  async getKafiOverview(params?: { range_start?: string; range_end?: string }): Promise<KafiOverview> {
    const { data } = await masterApi.get('/admin/kafi/overview', { params })
    return data
  },

  async getKafiTenants(params?: { metric?: string; cursor?: string; limit?: number; range_start?: string; range_end?: string }): Promise<{ items: KafiRankingRow[]; next_cursor: string | null }> {
    const { data } = await masterApi.get('/admin/kafi/tenants', { params })
    return { items: data.items || [], next_cursor: data.next_cursor || null }
  },

  async getKafiInfrastructure(): Promise<Record<string, unknown>> {
    const { data } = await masterApi.get('/admin/kafi/infrastructure')
    return data
  },

  async getKafiCommands(params?: Record<string, unknown>): Promise<{ items: Record<string, unknown>[] }> {
    const { data } = await masterApi.get('/admin/kafi/commands', { params })
    return data
  },

  async getKafiOutbound(params?: Record<string, unknown>): Promise<{ items: Record<string, unknown>[] }> {
    const { data } = await masterApi.get('/admin/kafi/outbound', { params })
    return data
  },

  async getKafiIssues(): Promise<{ items: Record<string, unknown>[] }> {
    const { data } = await masterApi.get('/admin/kafi/issues')
    return data
  },

  async getKafiProviders(params?: Record<string, unknown>): Promise<Record<string, unknown>> {
    const { data } = await masterApi.get('/admin/kafi/providers', { params })
    return data
  },

  async cancelKafiCommand(id: string, reason: string): Promise<Record<string, unknown>> {
    const { data } = await masterApi.post(`/admin/kafi/commands/${id}/cancel`, { reason })
    return data
  },

  async retryKafiOutbound(id: string, reason: string): Promise<Record<string, unknown>> {
    const { data } = await masterApi.post(`/admin/kafi/outbound/${id}/retry`, { reason })
    return data
  },
}
